<template>
  <div class="ct-page">
    <div v-if="loading" class="d-flex justify-content-center p-5">
      <div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="naoEncontrado" class="container py-5 text-center">
      <i class="bi bi-file-earmark-x" style="font-size: 3rem;"></i>
      <h2 class="mt-3">Contrato não encontrado</h2>
      <p class="text-muted">Confira o link com a locadora.</p>
    </div>

    <div v-else class="container py-4 ct-narrow">
      <header class="d-flex align-items-center gap-3 mb-3">
        <img v-if="contrato.tenant.logo" :src="contrato.tenant.logo" :alt="contrato.tenant.name" class="ct-logo">
        <div>
          <h1 class="h4 mb-0">{{ contrato.tenant.name }}</h1>
          <span class="text-muted small">Contrato de locação</span>
        </div>
      </header>

      <div v-if="contrato.status === 'CANCELED'" class="alert alert-warning d-print-none">
        Este contrato foi cancelado pela locadora. Peça um novo link.
      </div>

      <!-- Texto do contrato (texto puro, exibido escapado) -->
      <article class="ct-paper ct-print-area">
        <div class="ct-content">{{ contrato.content }}</div>

        <div v-if="contrato.status === 'SIGNED'" class="ct-signature">
          <div><strong>Aceite eletrônico</strong></div>
          <div>{{ contrato.signer_name }} — CPF/CNPJ {{ contrato.signer_document }}</div>
          <div>Assinado em {{ dataHora(contrato.signed_at) }}</div>
          <div class="small text-muted">Código de verificação: {{ contrato.content_hash }}</div>
        </div>
      </article>

      <!-- Assinatura -->
      <form v-if="contrato.status === 'PENDING'" class="card mt-3 d-print-none" @submit.prevent="assinar">
        <div class="card-body row g-3">
          <h2 class="h5 mb-0">Assinar</h2>
          <div class="col-md-7">
            <label class="form-label" for="txtNome">Nome completo</label>
            <input v-model="form.signer_name" required minlength="3" maxlength="255" class="form-control" id="txtNome">
          </div>
          <div class="col-md-5">
            <label class="form-label" for="txtDoc">CPF ou CNPJ</label>
            <input v-model="form.signer_document" required maxlength="20" class="form-control" id="txtDoc" inputmode="numeric">
          </div>
          <div class="col-12">
            <div class="form-check">
              <input v-model="form.accept" required class="form-check-input" type="checkbox" id="chkAceite">
              <label class="form-check-label" for="chkAceite">
                Li e concordo com todas as cláusulas deste contrato e aceito assiná-lo eletronicamente.
              </label>
            </div>
          </div>
          <div class="col-12">
            <button type="submit" class="btn btn-success btn-lg w-100" :disabled="assinando || !form.accept">
              <span v-if="assinando" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Assinar contrato
            </button>
            <p class="small text-muted mt-2 mb-0">Registramos data, hora e endereço IP do aceite.</p>
          </div>
        </div>
      </form>

      <div v-if="contrato.status === 'SIGNED'" class="text-center mt-3 d-print-none">
        <p class="text-success"><i class="bi bi-patch-check-fill me-1"></i>Contrato assinado.</p>
        <button class="btn btn-outline-secondary" @click="imprimir"><i class="bi bi-printer me-2"></i>Imprimir / salvar PDF</button>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { contracts } from '@/models/contracts';
import { useToastStore } from '@/stores/toastStore';
import { parseError } from '@/utils/errorHelper';

export default {
  name: "ContractSignView",
  setup() {

    const route = useRoute();
    const toastStore = useToastStore();
    const model = contracts();
    const token = route.params.token;

    const loading = ref(true);
    const naoEncontrado = ref(false);
    const assinando = ref(false);
    const contrato = ref(null);
    const form = reactive({ signer_name: '', signer_document: '', accept: false });

    async function assinar() {
      assinando.value = true;
      try {
        contrato.value = await model.sign(token, { ...form });
        toastStore.success('Contrato assinado com sucesso.');
      } catch (error) {
        toastStore.error(parseError(error));
      }
      assinando.value = false;
    }

    function imprimir() {
      window.print();
    }

    function dataHora(valor) {
      return valor ? new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
    }

    onMounted(async () => {
      try {
        contrato.value = await model.getByToken(token);
        document.title = 'Contrato - ' + contrato.value.tenant.name;
      } catch (error) {
        naoEncontrado.value = true;
      }
      loading.value = false;
    });

    return { loading, naoEncontrado, assinando, contrato, form, assinar, imprimir, dataHora };
  }
}
</script>

<style scoped>
.ct-page {
  min-height: 100vh;
  background: #f6f7f9;
}

.ct-narrow {
  max-width: 820px;
}

.ct-logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 10px;
}

.ct-paper {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 32px;
}

.ct-content {
  white-space: pre-wrap;
  font-family: Georgia, 'Times New Roman', serif;
  line-height: 1.6;
}

.ct-signature {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #333;
  word-break: break-all;
}

@media (max-width: 576px) {
  .ct-paper {
    padding: 18px;
  }
}
</style>

<!-- Impressão: só o contrato (há estilos globais de impressão de outras telas) -->
<style>
@media print {
  body * {
    visibility: hidden;
  }

  .ct-print-area,
  .ct-print-area * {
    visibility: visible;
  }

  .ct-print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    border: none !important;
  }
}
</style>
